import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Sliders, Edit, HelpCircle, Star, BarChart3, FileText, 
  MessageSquare, Link, MapPin, Clock, Smartphone, CreditCard, 
  Users, Award, Crown, Calendar, Coins, Bell, Mail
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin/users', title: 'User Management', desc: 'View and edit all users', testId: 'users-management-card' },
  { path: '/admin/credentials', title: 'Credentials', desc: 'Verify tradesperson credentials', icon: Shield, testId: 'credentials-management-card' },
  { path: '/admin/moderation', title: 'Profile Moderation', desc: 'Review flagged worker profiles', icon: Shield, borderColor: 'orange', badge: 'REVIEW', testId: 'moderation-queue-card' },
  { path: '/admin/settings', title: 'App Settings', desc: 'Configure pricing & payments', icon: Sliders, testId: 'settings-card' },
  { path: '/admin/email-broadcast', title: 'Email Broadcast', desc: 'Send emails to all trades or employers', icon: Mail, borderColor: 'orange', badge: 'NEW', testId: 'email-broadcast-card' },
  { path: '/admin/faqs', title: 'FAQ Management', desc: 'Manage frequently asked questions', icon: HelpCircle, testId: 'faqs-card' },
  { path: '/admin/reviews', title: 'Review Management', desc: 'View and edit tradesperson reviews', icon: Star, testId: 'reviews-card' },
  { path: '/admin/reports', title: 'Reports & Analytics', desc: 'View platform metrics and export data', icon: BarChart3, testId: 'reports-card' },
  { path: '/admin/weekly-reports', title: 'Weekly Reports', desc: 'View and send automated weekly reports', icon: Calendar, testId: 'weekly-reports-card' },
  { path: '/admin/email-settings', title: 'Email Settings', desc: 'Configure email sending and templates', icon: MessageSquare, testId: 'email-settings-card' },
  { path: '/admin/on-call-settings', title: 'On Call Settings', desc: 'Configure On Call feature access and launch mode', iconColor: 'emerald', testId: 'on-call-settings-card', customIcon: 'phone' },
  { path: '/admin/hiring-settings', title: 'Hiring Control', desc: 'Enable/disable hiring and set countdown', borderColor: 'green', badge: 'NEW', testId: 'hiring-settings-card' },
  { path: '/admin/cms', title: 'Page Content (Rich)', desc: 'Edit rich page content with images', icon: FileText, testId: 'cms-card' },
  { path: '/admin/site-content', title: 'Site Text Content', desc: 'Edit all headlines, CTAs, helper text', icon: Edit, borderColor: 'orange', badge: 'NEW', testId: 'site-content-card' },
  { path: '/admin/feedback', title: 'Feedback Management', desc: 'View and manage user feedback', icon: MessageSquare, testId: 'feedback-card' },
  { path: '/admin/social-links', title: 'Social Media Links', desc: 'Configure footer social links', icon: Link, testId: 'social-links-card' },
  { path: '/admin/regions', title: 'Expansion Intelligence', desc: 'Regional rollout & launch readiness', icon: MapPin, borderColor: 'orange', badge: 'NEW', testId: 'regions-card' },
  { path: '/admin/waitlist', title: 'Waitlist Management', desc: 'View, filter, and manage waitlist entries', icon: Clock, testId: 'waitlist-card' },
  { path: '/admin/pwa-settings', title: 'PWA Settings', desc: 'Configure mobile app experience', icon: Smartphone, testId: 'pwa-settings-card' },
  { path: '/admin/offer-templates', title: 'Offer Templates', desc: 'Create global offer templates for employers', icon: FileText, testId: 'offer-templates-card' },
  { path: '/admin/support-inbox', title: 'Support Inbox', desc: 'View and respond to user messages', icon: MessageSquare, testId: 'support-inbox-card' },
  { path: '/admin/chat-credit-logs', title: 'Chat Credit Logs', desc: 'View audit log of manually added credits', icon: CreditCard, testId: 'chat-credit-logs-card' },
  { path: '/admin/dashboard-banners', title: 'Dashboard Banners', desc: 'Manage notices for Trades & Employer dashboards', icon: Bell, borderColor: 'blue', badge: 'NEW', testId: 'dashboard-banners-card' },
];

const REFERRAL_ITEMS = [
  { path: '/admin/employers', title: 'Employer Approvals', desc: 'Approve, reject, suspend or restore employers', icon: Shield, color: 'orange', testId: 'employer-approvals-card' },
  { path: '/admin/funnel', title: 'Launch Funnel', desc: 'Signup → onboarding → searchable → hire conversion', icon: Clock, color: 'orange', testId: 'launch-funnel-card' },
  { path: '/admin/pending-approvals', title: 'Pending Approvals', desc: 'Review and approve new user signups', icon: Clock, color: 'yellow', testId: 'pending-approvals-card' },
  { path: '/admin/referrals', title: 'Referral Monitoring', desc: 'Track and qualify user referrals', icon: Users, color: 'green', testId: 'referrals-card' },
  { path: '/admin/grandfather-rules', title: 'Grandfather Rules', desc: 'Configure founding member criteria', icon: Award, color: 'amber', testId: 'grandfather-rules-card' },
  { path: '/admin/grandfather-members', title: 'Founding Members', desc: 'View and manage founding members', icon: Crown, color: 'amber', testId: 'grandfather-members-card' },
];

export default function AdminQuickActions() {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`card hover:border-${item.borderColor || 'orange'}-500 transition-all cursor-pointer text-left ${item.borderColor ? `border-2 border-${item.borderColor}-200` : ''}`}
            data-testid={item.testId}
          >
            <div className="flex items-center gap-2 mb-2">
              {Icon && <Icon className={`h-5 w-5 text-${item.iconColor || 'orange'}-500`} />}
              <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
              {item.badge && (
                <span className={`text-xs bg-${item.borderColor || 'orange'}-100 text-${item.borderColor || 'orange'}-700 px-2 py-0.5 rounded-full font-medium`}>
                  {item.badge}
                </span>
              )}
            </div>
            <p className="text-slate-600 text-sm">{item.desc}</p>
          </button>
        );
      })}

      {/* Referral System Section */}
      {REFERRAL_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`card hover:border-${item.color}-500 transition-all cursor-pointer text-left border-${item.color}-200 bg-${item.color}-50/30`}
            data-testid={item.testId}
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`h-5 w-5 text-${item.color}-600`} />
              <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
            </div>
            <p className="text-slate-600 text-sm">{item.desc}</p>
          </button>
        );
      })}

      <button
        onClick={() => navigate('/admin/management')}
        className="card hover:border-red-500 transition-all cursor-pointer text-left border-red-200 bg-red-50/30"
        data-testid="admin-management-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-5 w-5 text-red-500" />
          <h3 className="text-lg font-bold text-slate-900">Admin Management</h3>
        </div>
        <p className="text-slate-600 text-sm">Manage admins, roles, permissions & feature toggles</p>
      </button>
    </div>
  );
}
